import { useEffect, useState } from 'react';
import { createCBDCWallet, listCBDCBanks, listSettlements, requestSettlement } from '../../../engine/cbdcAdapter';
import { useCompanyStore } from '../stores/company';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorBanner from '../components/common/ErrorBanner';

export function CBDCDesk() {
  const company = useCompanyStore((s) => s.companies.find(c => c.id === s.activeId) ?? null);
  const [banks, setBanks] = useState<Array<{id:string;name:string}>>([]);
  const [wallet, setWallet] = useState<string>('');
  const [amount, setAmount] = useState(1000);
  const [purpose, setPurpose] = useState('ESG');
  const [ref, setRef] = useState('REF-001');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const banksList = await listCBDCBanks();
        setBanks(banksList);
      } catch (e) {
        console.error('Failed to load CBDC banks:', e);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      if (company) {
        try {
          const w = await createCBDCWallet(company.id);
          setWallet(w.walletId);
          const settlements = await listSettlements(company.id);
          setRows(settlements);
        } catch (e) {
          console.error('Failed to load CBDC wallet:', e);
        }
      }
    })();
  }, [company?.id]);

  const submit = async () => {
    if (!company) {
      setError(new Error('No company selected'));
      return;
    }
    
    setLoading(true);
    setError(null);
    try {
      await requestSettlement(company.id, amount, purpose, ref);
      const settlements = await listSettlements(company.id);
      setRows(settlements);
    } catch (e) {
      const error = e instanceof Error ? e : new Error('Failed to request settlement');
      setError(error);
      console.error('CBDC settlement failed:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="font-orbitron text-evat-gold">CBDC Settlement</div>
      <div className="text-sm text-neutral-400">Wallet: <span className="text-evat-neon">{wallet || '-'}</span></div>
      
      <ErrorBanner error={error} onRetry={submit} showRetry={!!error} />
      
      {loading && <LoadingSpinner size="sm" text="Processing settlement..." />}
      
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 text-sm">
        <label className="flex items-center gap-2">Amount €
          <input
            type="number"
            value={amount}
            onChange={(e)=>setAmount(Number(e.target.value))}
            disabled={loading}
            className="bg-black border border-evat-neon text-evat-neon rounded px-2 py-1 disabled:opacity-50"
          />
        </label>
        <label className="flex items-center gap-2">Purpose
          <input
            value={purpose}
            onChange={(e)=>setPurpose(e.target.value)}
            disabled={loading}
            className="bg-black border border-evat-neon text-evat-neon rounded px-2 py-1 disabled:opacity-50"
          />
        </label>
        <label className="flex items-center gap-2">Reference
          <input
            value={ref}
            onChange={(e)=>setRef(e.target.value)}
            disabled={loading}
            className="bg-black border border-evat-neon text-evat-neon rounded px-2 py-1 disabled:opacity-50"
          />
        </label>
        <button
          onClick={submit}
          disabled={loading}
          className="px-3 py-1 rounded border border-evat-neon text-evat-neon hover:shadow-neon disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Processing...' : 'Request Settlement'}
        </button>
      </div>
      <div className="border border-neutral-700 rounded p-2 text-xs max-h-64 overflow-auto">
        {rows.map(r => (
          <div key={r.id} className="flex justify-between"><span>{new Date(r.timestamp).toLocaleString()}</span><span>€{r.amountEUR} • {r.purposeCode} • {r.referenceId}</span></div>
        ))}
        {rows.length===0 && <div className="text-neutral-500">No settlements</div>}
      </div>
    </div>
  );
}

export default CBDCDesk;














